import {
    AwsNetworkList,
    AwsNetworkOutput,
    AwsStorageAnnualCosts,
    DatabaseStorage,
    EbsAnnualCosts,
    EbsStorageOutput,
    EfsAnnualCosts,
    EfsStorageOutput,
    RdsAnnualCosts,
    RdsProvisionedAnnualCosts,
    RdsStorageOutput,
    S3AnnualCosts,
    S3IAAnnualCosts,
    S3StorageOutput,
    VirtualServerStorageOutput,
} from '@amzn/awscat-portfolio-assessment-types';
import { Box, Header, Table, TableProps, TabsProps } from '@amzn/awsui-components-react';

import { constants } from '../../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumber, formatNumberAsString } from '../../../../common/LocalizationHelper';
import AWSCATTable from '../../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../../common/table/types';
import { getCell } from '../../../common/awsui-table-helper';
import { generateCashFlowTable } from '../../common/Common';
import { CashFlowData } from '../../common/types';

const emptySummaryState = (resourceName: string): JSX.Element => (
    <Box textAlign='center' color='inherit'>
        <b>{constants.NO} {resourceName}</b>
        <Box
            padding={{ bottom: 's' }}
            variant='p'
            color='inherit'
        >
            {constants.NO_RESULTS_TO_DISPLAY}
        </Box>
    </Box>
);

const awsStorageEbsTableCols = (currency: string): TableProps.ColumnDefinition<EbsStorageOutput>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: EbsStorageOutput) => getCell(item.type, false, 'left'),
}, {
    id: 'sharedStorageSize',
    header: getCell(constants.AWS_STORAGE_SHARED_SIZE),
    cell: (item: EbsStorageOutput) => getCell(formatNumberAsString(item.sharedUsableSize, {maximumFractionDigits: 0}) + ' GB'),
}, {
    id: 'virtualStorageSize',
    header: getCell(constants.AWS_STORAGE_VIRTUAL_SIZE),
    cell: (item: EbsStorageOutput) => getCell(formatNumberAsString(item.virtualServerUsableSize, {maximumFractionDigits: 0}) + ' GB'),
}, {
    id: 'physicalSorageSize',
    header: getCell(constants.AWS_STORAGE_PHYSICAL_SIZE),
    cell: (item: EbsStorageOutput) => getCell(formatNumberAsString(item.physicalServerUsableSize, {maximumFractionDigits: 0}) + ' GB'),
}, {
    id: 'totalIops',
    header: getCell(constants.AWS_STORAGE_TOTAL_IOPS),
    cell: (item: EbsStorageOutput) => getCell(formatNumberAsString(item.maxIops, {maximumFractionDigits: 0})),
}, {
    id: 'cost',
    header: getCell(constants.COST),
    cell: (item: EbsStorageOutput) => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
}];

export const buildAwsEbsStorageSummaryTable = (ebs: EbsStorageOutput[], currency: string): JSX.Element => {
    return (
        <Table
            header={<Header variant='h2'>{constants.AWS_STORAGE_EBS_TITLE}</Header>}
            columnDefinitions={awsStorageEbsTableCols(currency)}
            items={ebs}
            empty={emptySummaryState(constants.AWS_STORAGE_EBS_TITLE)}
            sortingDisabled
        />
    ); 
};

const awsStorageRdsTableCols = (currency: string): TableProps.ColumnDefinition<RdsStorageOutput>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: RdsStorageOutput) => getCell(item.type, false, 'left'),
}, {
    id: 'storageSize',
    header: getCell(constants.AWS_STORAGE_SIZE),
    cell: (item: RdsStorageOutput) => getCell(formatNumberAsString(item.storageSize, {maximumFractionDigits: 0}) + ' GB'),
}, {
    id: 'iops',
    header: getCell(constants.AWS_STORAGE_IOPS),
    cell: (item: RdsStorageOutput) => item.iops != undefined ? getCell(formatNumberAsString(item.iops, {maximumFractionDigits: 0})) : getCell('-'),
}, {
    id: 'cost',
    header: getCell(constants.COST),
    cell: (item: RdsStorageOutput) => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
}];

export const buildAwsRdsStorageSummaryTable = (rds: RdsStorageOutput[], currency: string): JSX.Element => {
    return (
        <Table
            header={<Header variant='h2'>{constants.AWS_STORAGE_RDS_TITLE}</Header>}
            columnDefinitions={awsStorageRdsTableCols(currency)}
            items={rds}
            empty={emptySummaryState(constants.AWS_STORAGE_RDS_TITLE)}
            sortingDisabled
        />
    ); 
};

const awsStorageObjectTableCols = (currency: string): TableProps.ColumnDefinition<S3StorageOutput>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: S3StorageOutput) => getCell(item.type, false, 'left'),
}, {
    id: 'rawSize',
    header: getCell(constants.AWS_STORAGE_RAW_SIZE),
    cell: (item: S3StorageOutput) => getCell(formatNumberAsString(item.rawSize, {maximumFractionDigits: 0}) + ' GB'),
}, {
    id: 'usableSize',
    header: getCell(constants.AWS_STORAGE_USABLE_SIZE),
    cell: (item: S3StorageOutput) => getCell(formatNumberAsString(item.usableSize, {maximumFractionDigits: 0}) + 'GB'),
}, {
    id: 'cost',
    header: getCell(constants.COST),
    cell: (item: S3StorageOutput) => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
}];

export const buildAwsObjectStorageSummaryTable = (objectItems: S3StorageOutput[], currency: string): JSX.Element => {
    return (
        <Table
            header={<Header variant='h2'>{constants.AWS_STORAGE_OBJECT_TITLE}</Header>}
            columnDefinitions={awsStorageObjectTableCols(currency)}
            items={objectItems}
            empty={emptySummaryState(constants.AWS_STORAGE_OBJECT_TITLE)}
            sortingDisabled
        />
    ); 
};

const awsStorageEfsTableCols = (currency: string): TableProps.ColumnDefinition<EfsStorageOutput>[] => [{
    id: 'type',
    header: constants.TCO_PAGE_SUMMARY_TYPE,
    cell: (item: EfsStorageOutput) => getCell(item.type, false, 'left'),
}, {
    id: 'rawSize',
    header: getCell(constants.AWS_STORAGE_RAW_SIZE),
    cell: (item: EfsStorageOutput) => getCell(formatNumberAsString(item.rawSize, {maximumFractionDigits: 0}) + ' GB'),
}, {
    id: 'usableSize',
    header: getCell(constants.AWS_STORAGE_USABLE_SIZE),
    cell: (item: EfsStorageOutput) => getCell(formatNumberAsString(item.usableSize, {maximumFractionDigits: 0}) + 'GB'),
}, {
    id: 'cost',
    header: getCell(constants.COST),
    cell: (item: EfsStorageOutput) => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
}];

export const buildAwsEfsStorageSummaryTable = (efs: EfsStorageOutput[], currency: string): JSX.Element => {
    return (
        <Table
            header={<Header variant='h2'>{constants.AWS_STORAGE_EFS_TITLE}</Header>}
            columnDefinitions={awsStorageEfsTableCols(currency)}
            items={efs}
            empty={emptySummaryState(constants.AWS_STORAGE_EFS_TITLE)}
            sortingDisabled
        />
    );
};

const awsNetworkTableCols = (currency: string): TableProps.ColumnDefinition<AwsNetworkList>[] => [{
    id: 'networkSize',
    header: constants.NETWORK_SIZE_OF_PIPE,
    cell: (item: AwsNetworkList) =>
        getCell(formatNumberAsString(item.sizeOfPipeMB, {maximumFractionDigits: 0}) + ' ' + constants.MBITS_PER_SECOND, false, 'left'),
}, {
    id: 'peakAvgRatio',
    header: getCell(constants.NETWORK_RATIO),
    cell: (item: AwsNetworkList) => getCell(formatNumberAsString(item.ratio, {maximumFractionDigits: 2})),
}, {
    id: 'dataTransfer',
    header: getCell(constants.AWS_NETWORK_DATA_TRANSFER_PER_MONTH),
    cell: (item: AwsNetworkList) => getCell(formatNumberAsString(item.totalDataTransferPerMonth, {maximumFractionDigits: 0}) + 'GB'),
}, {
    id: 'redundantPipe',
    header: getCell(constants.NETWORK_REDUNDANT_PIPE),
    cell: (item: AwsNetworkList) => getCell(item.redundantPipe ? constants.YES : constants.NO),
}, {
    id: 'cost',
    header: getCell(constants.COST),
    cell: (item: AwsNetworkList) => getCell(formatCurrencyNoDecimals(item.totalDataTransferCost, currency)),
}];

export const buildAwsNetworkSummaryTable = (network: AwsNetworkList[], currency: string): JSX.Element => {
    return (
        <Table
            columnDefinitions={awsNetworkTableCols(currency)}
            items={network}
            empty={emptySummaryState(constants.NETWORK)}
            sortingDisabled
        />
    );
};

type EbsCashFlowResources = {
    id: keyof EbsAnnualCosts,
    nestId?: Exclude<keyof EbsAnnualCosts['ebsSnapshot'], 'year'>,
    label: string,
    unit?: string,
};

export const generateEbsCashFlowItems = (
    ebsAnnualCosts: EbsAnnualCosts[], snapshotCompressionRatio: number, hasThroughput?: boolean, hasIOPS?: boolean,
): CashFlowData[] => {
    const ebsResources: EbsCashFlowResources[] = [{
        id: 'sharedUsableStorageDiskSize',
        label: constants.AWS_STORAGE_SHARED_SIZE,
        unit: 'GB',
    }, {
        id: 'virtualUsableServerStorageDiskSize',
        label: constants.AWS_STORAGE_VIRTUAL_SIZE,
        unit: 'GB',
    }, {
        id: 'physicalUsableServerStorageDiskSize',
        label: constants.AWS_STORAGE_PHYSICAL_SIZE,
        unit: 'GB',
    }, {
        id: 'usableStorageDiskSize',
        label: constants.AWS_STORAGE_TOTAL_SIZE,
        unit: 'GB',
    }, {
        id: 'ebsCost',
        label: constants.VOLUME_COST,
    }, {
        id: 'ebsSnapshot',
        nestId: 'diskSizeWithGrowth',
        label: constants.AWS_EBS_SNAPSHOT_SIZE,
        unit: 'GB',
    }, {
        id: 'ebsSnapshot',
        nestId: 'initialSnapshotCost',
        label: constants.AWS_EBS_SNAPSHOT_COST,
    }, {
        id: 'ebsSnapshot',
        nestId: 'subsequentSnapshotCost',
        label: constants.AWS_EBS_SUBS_SNAPSHOT_COST,
    }];

    if (hasIOPS) {
        ebsResources.splice(5, 0, {
            id: 'iopsCost',
            label: constants.AWS_EBS_IOPS_COST,
        });
    }

    if (hasThroughput) {
        ebsResources.splice(6, 0, {
            id: 'throughputCost',
            label: constants.AWS_EBS_THROUGHPUT_COST,
        });
    }

    const ebsCashFlowItems: CashFlowData[] = [];
    ebsResources.forEach(resource => {
        ebsCashFlowItems.push({ type: resource.label } as CashFlowData);
    });

    const numYears = ebsAnnualCosts.length;
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        ebsResources.forEach((resource, resourceIndex) => {
            if (resource.id === 'ebsSnapshot') {
                if (!resource.nestId) {
                    return;
                }
                ebsCashFlowItems[resourceIndex]['year' + yearIndex] = ebsAnnualCosts[yearIndex][resource.id][resource.nestId];
                // TODO: V821834431, Remove this logic once EBS calculations are consolidated and fixed
                if (resource.nestId === 'diskSizeWithGrowth') {
                    ebsCashFlowItems[resourceIndex]['year' + yearIndex] /= snapshotCompressionRatio;
                }
            } else {
                ebsCashFlowItems[resourceIndex]['year' + yearIndex] = ebsAnnualCosts[yearIndex][resource.id];
            }
            
            ebsCashFlowItems[resourceIndex].otherUnit = resource.unit;
        });
    }

    return ebsCashFlowItems;
};

const generateEfsCashFlowItems = (efsAnnualCosts: EfsAnnualCosts[]): CashFlowData[] => {
    const sizeCashFlow = {
        type: constants.STORAGE_SIZE_IN_GB,
        otherUnit: 'GB',
    };
    const costCashFlow = {
        type: constants.COST,
    };
    const numYears = efsAnnualCosts.length;
    
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        (sizeCashFlow as CashFlowData)['year' + yearIndex] = efsAnnualCosts[yearIndex].usableStorageDiskSize;
        (costCashFlow as CashFlowData)['year' + yearIndex] = efsAnnualCosts[yearIndex].efsCost;
    }
    return [
        sizeCashFlow as CashFlowData,
        costCashFlow as CashFlowData,
    ];
};

type RdsCashFlowResources<T extends RdsAnnualCosts | RdsProvisionedAnnualCosts> = {
    label: string,
    unit?: string,
} & (T extends RdsProvisionedAnnualCosts ? { id: keyof RdsProvisionedAnnualCosts } : { id: keyof RdsAnnualCosts });

const generaterRdsCashFlowItem = (rdsAnnualCosts: RdsAnnualCosts[] | RdsProvisionedAnnualCosts[], isIOPS?: boolean): CashFlowData[] => {
    const rdsResources: RdsCashFlowResources<RdsAnnualCosts>[] = [{
        id: 'storageSize',
        label: constants.AWS_STORAGE_SIZE,
        unit: 'GB',
    }, {
        id: 'storageCost',
        label: constants.STORAGE_COST,
    }, {
        id: 'totalCost',
        label: constants.COST,
    }];

    if (isIOPS) {
        (rdsResources as RdsCashFlowResources<RdsProvisionedAnnualCosts>[]).splice(1, 0, {
            id: 'iops',
            label: constants.AWS_STORAGE_IOPS,
            unit: '',
        });

        (rdsResources as RdsCashFlowResources<RdsProvisionedAnnualCosts>[]).splice(3, 0, {
            id: 'iopsCost',
            label: constants.AWS_EBS_IOPS_COST,
        });
    }

    const rdsCashFlowItems: CashFlowData[] = [];
    rdsResources.forEach(resource => {
        rdsCashFlowItems.push({ type: resource.label } as CashFlowData);
    });

    const numYears = rdsAnnualCosts.length;
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        rdsResources.forEach((resource, resourceIndex) => { 
            rdsCashFlowItems[resourceIndex]['year' + yearIndex] = rdsAnnualCosts[yearIndex][resource.id];
            rdsCashFlowItems[resourceIndex].otherUnit = resource.unit;
        });
    }

    return rdsCashFlowItems;
};

const generateS3CashFlowItems = (s3AnnualCosts: S3AnnualCosts[]): CashFlowData[] => {
    const sizeCashFlow = {
        type: constants.STORAGE_SIZE_IN_GB,
        otherUnit: 'GB',
    };
    const costCashFlow = {
        type: constants.COST,
    };
    const numYears = s3AnnualCosts.length;
    
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        (sizeCashFlow as CashFlowData)['year' + yearIndex] = s3AnnualCosts[yearIndex].usableStorageDiskSize;
        (costCashFlow as CashFlowData)['year' + yearIndex] = s3AnnualCosts[yearIndex].s3Cost;
    }
    return [
        sizeCashFlow as CashFlowData,
        costCashFlow as CashFlowData,
    ];
};

type S3IACashFlowResources = {
    id: keyof S3IAAnnualCosts,
    label: string,
    unit?: string,
};

const generateS3IACashFlowItem = (s3IAAnnualCosts: S3IAAnnualCosts[]): CashFlowData[] => {
    const s3IaResources: S3IACashFlowResources[] = [{
        id: 'usableStorageDiskSize',
        label: constants.AWS_S3_IA_BILLABLE_SIZE,
        unit: 'GB',
    }, {
        id: 'volumeCost',
        label: constants.VOLUME_COST,
    }, {
        id: 'lifecycleCost',
        label: constants.AWS_S3_IA_LIFECYCLE_COST,
    }, {
        id: 'putRequestsCost',
        label: constants.AWS_S3_IA_PUT_COST,
    }, {
        id: 'listRequestsCost',
        label: constants.AWS_S3_IA_LIST_COST,
    }, {
        id: 'retrieveRequestsCost',
        label: constants.AWS_S3_IA_RETRIEVE_COST,
    }, {
        id: 'getRequestsCost',
        label: constants.AWS_S3_IA_GET_COST,
    }, {
        id: 'shortTermStorage',
        label: constants.AWS_S3_IA_SHORT_TERM_STORAGE,
        unit: 'GB',
    }, {
        id: 'additionalChargesShortTermStorage',
        label: constants.AWS_S3_IA_SHORT_TERM_COST,
    }];

    const s3IaCashFlowItems: CashFlowData[] = [];
    s3IaResources.forEach(resource => {
        s3IaCashFlowItems.push({ type: resource.label } as CashFlowData);
    });

    const numYears = s3IAAnnualCosts.length;
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        s3IaResources.forEach((resource, resourceIndex) => { 
            s3IaCashFlowItems[resourceIndex]['year' + yearIndex] = s3IAAnnualCosts[yearIndex][resource.id];
            s3IaCashFlowItems[resourceIndex].otherUnit = resource.unit;
        });
    }

    return s3IaCashFlowItems;
};

const generateNetworkCashFlowItems = (network: AwsNetworkOutput): CashFlowData[] => {
    const sizeCashFlow = {
        type: constants.AWS_NETWORK_SIZE_WITH_UNIT,
        otherUnit: 'Mbits',
    };
    const dataTransferCashFlow = {
        type: constants.AWS_NETWORK_DATA_TRANSFER,
        otherUnit: 'GB',
    };
    const costCashFlow = {
        type: constants.COST,
    };

    const numYears = network.cashFlow.yearly.length;
    
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        (sizeCashFlow as CashFlowData)['year' + yearIndex] = network.summary.sizeOfPipe[yearIndex];
        (dataTransferCashFlow as CashFlowData)['year' + yearIndex] = network.summary.dataTransfer[yearIndex];
        (costCashFlow as CashFlowData)['year' + yearIndex] = network.cashFlow.yearly[yearIndex];
    }
    return [
        sizeCashFlow as CashFlowData,
        dataTransferCashFlow as CashFlowData,
        costCashFlow as CashFlowData,
    ];
};

const ebsVolumePerServerCols = (currency: string): ColumnDefinition<VirtualServerStorageOutput>[] => {
    const ebsColumnsDefs = [{
        id: 'serverId',
        header: constants.SERVER_ID,
        cell: (item: VirtualServerStorageOutput) => item.serverId,
        type: ColumnTypes.TEXT,
    }, {
        id: 'volumeTypeApi',
        header: constants.AWS_EBS_TYPE,
        cell: (item: VirtualServerStorageOutput) => item.volumeTypeApi,
        type: ColumnTypes.TEXT,
    }, {
        id: 'volumeSizeInGB',
        header: constants.VOLUME_GB,
        cell: (item: VirtualServerStorageOutput) => getCell(formatNumberAsString(item.volumeSizeInGB, {maximumFractionDigits: 0})),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'volumeCost',
        header: constants.VOLUME_COST,
        cell: (item: VirtualServerStorageOutput) => getCell(formatCurrencyNoDecimals(item.volumeCost, currency)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'iops',
        header: constants.AWS_STORAGE_IOPS,
        cell: (item: VirtualServerStorageOutput) => getCell(formatNumber(item.iops)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'iopsCost',
        header: constants.AWS_EBS_IOPS_COST,
        cell: (item: VirtualServerStorageOutput) => getCell(formatCurrencyNoDecimals(item.iopsCost, currency)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'throughput',
        header: constants.AWS_EBS_THROUGHPUT_MB,
        cell: (item: VirtualServerStorageOutput) => getCell(formatNumber(item.throughput)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'throughputCost',
        header: constants.AWS_EBS_THROUGHPUT_COST,
        cell: (item: VirtualServerStorageOutput) => getCell(formatCurrencyNoDecimals(item.throughputCost, currency)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'snapshotSizeInGB',
        header: constants.AWS_EBS_SNAPSHOT_SIZE,
        cell: (item: VirtualServerStorageOutput) => getCell(formatNumberAsString(item.snapshotSizeInGB, {maximumFractionDigits: 0})),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'snapshotCost',
        header: constants.AWS_EBS_SNAPSHOT_COST,
        cell: (item: VirtualServerStorageOutput) => getCell(formatCurrencyNoDecimals(item.snapshotCost, currency)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        cell: (item: VirtualServerStorageOutput) => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
        type: ColumnTypes.NUMBER,
    }];

    const colsWithText = ebsColumnsDefs.map(col => ({
        ...col,
        text: col.header,
    }));

    return colsWithText;
};


const buildEbsVolumePerServerTable = (serverStorage: VirtualServerStorageOutput[], currency: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.AWS_EBS_VOLUME}
            items={serverStorage}
            columnDefinitions={ebsVolumePerServerCols(currency)}
            resizeable
            useDefaultDownload
            enableAutoSorting
            autoGenerateVisibleContent
        />
    );
};

const storageRecommendationCols = (currency: string): ColumnDefinition<DatabaseStorage>[] => {
    const storageRecommendationColDefs = [
        {
            id: 'databaseId',
            header: constants.DATABASE_ID,
            data: (item: DatabaseStorage) => item.databaseId ?? '-',
            cell: (item: DatabaseStorage) => item.databaseId ?? '-',
            type: ColumnTypes.TEXT,
        }, {
            id: 'volumeType',
            header: constants.VOLUME_TYPE,
            data: (item: DatabaseStorage) => item.volumeType ?? '-',
            cell: (item: DatabaseStorage) => item.volumeType ?? '-',
            type: ColumnTypes.TEXT,
        }, {
            id: 'storageSize',
            header: constants.AWS_STORAGE_SIZE,
            cell: (item: DatabaseStorage) => getCell(formatNumberAsString(item.storageSize, { maximumFractionDigits: 0 })),
            type: ColumnTypes.NUMBER,
        }, {
            id: 'IOPS',
            header: constants.AWS_STORAGE_IOPS,
            data: (item: DatabaseStorage) => item.IOPS ?? '',
            cell: (item: DatabaseStorage) => getCell(item.IOPS ? formatNumber(item.IOPS) : ''),
            type: ColumnTypes.NUMBER,
        }, {
            id: 'annualStorageCost',
            header: constants.STORAGE_COST,
            cell: (item: DatabaseStorage) => getCell(formatCurrencyNoDecimals(item.annualStorageCost, currency)),
            type: ColumnTypes.NUMBER,
        }, {
            id: 'annualIOPSCost',
            header: constants.AWS_EBS_IOPS_COST,
            data: (item: DatabaseStorage) => item.annualIOPSCost ?? '',
            cell: (item: DatabaseStorage) => getCell(item.annualIOPSCost ? formatCurrencyNoDecimals(item.annualIOPSCost, currency) : ''),
            type: ColumnTypes.NUMBER,
        }, {
            id: 'annualTotalCost',
            header: constants.ANNUAL_TOTAL_COST,
            cell: (item: DatabaseStorage) => getCell(formatCurrencyNoDecimals(item.annualTotalCost, currency)),
            type: ColumnTypes.NUMBER,
        }
    ];

    const colsWithText = storageRecommendationColDefs.map(col => ({
        ...col,
        text: col.header,
    }));

    return colsWithText;
};

const buildStorageRecommendationTable = (storageRecommendation: DatabaseStorage[], currency: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.AWS_DB_RECOMMENDATION}
            items={storageRecommendation}
            columnDefinitions={storageRecommendationCols(currency)}
            resizeable
            useDefaultDownload
            enableAutoSorting
            autoGenerateVisibleContent
        />
    );
};

export const buildAwsStorageNetworkTabs = (
    storageAnnualCost: AwsStorageAnnualCosts,
    serverStorage: VirtualServerStorageOutput[],
    storageRecommendations: DatabaseStorage[],
    network: AwsNetworkOutput,
    currency: string,
    snapshotCompressionRatio: number,
): TabsProps.Tab[] => {
    const storageAndNetworkTabs: TabsProps.Tab[] = [
        {
            id: 'ebsGeneralPurpose',
            label: constants.AWS_EBS_GP,
            content: generateCashFlowTable(
                generateEbsCashFlowItems(storageAnnualCost.ebsGeneralPurpose, snapshotCompressionRatio, true, true),
                storageAnnualCost.ebsGeneralPurpose.length,
                currency,
                constants.AWS_EBS_GP + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'ebsProvisionedIops',
            label: constants.AWS_EBS_PROVISIONED,
            content: generateCashFlowTable(
                generateEbsCashFlowItems(storageAnnualCost.ebsProvisionedIops, snapshotCompressionRatio, true),
                storageAnnualCost.ebsProvisionedIops.length,
                currency,
                constants.AWS_EBS_PROVISIONED + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'ebsThroughputOptimized',
            label: constants.AWS_EBS_THROUGHPUT,
            content: generateCashFlowTable(
                generateEbsCashFlowItems(storageAnnualCost.ebsThroughputOptimized, snapshotCompressionRatio),
                storageAnnualCost.ebsThroughputOptimized.length,
                currency,
                constants.AWS_EBS_THROUGHPUT + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'ebsColdHdd',
            label: constants.AWS_EBS_COLD_HDD,
            content: generateCashFlowTable(
                generateEbsCashFlowItems(storageAnnualCost.ebsColdHdd, snapshotCompressionRatio),
                storageAnnualCost.ebsColdHdd.length,
                currency,
                constants.AWS_EBS_COLD_HDD + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'efs',
            label: constants.AWS_EFS,
            content: generateCashFlowTable(
                generateEfsCashFlowItems(storageAnnualCost.efs),
                storageAnnualCost.efs.length,
                currency,
                constants.AWS_EFS + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'glacier',
            label: constants.AWS_S3_GLACIER,
            content: generateCashFlowTable(
                generateS3CashFlowItems(storageAnnualCost.glacier),
                storageAnnualCost.glacier.length,
                currency,
                constants.AWS_S3_GLACIER + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 's3',
            label: constants.AWS_S3_STANDARD,
            content: generateCashFlowTable(
                generateS3CashFlowItems(storageAnnualCost.s3),
                storageAnnualCost.s3.length,
                currency,
                constants.AWS_S3_STANDARD + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 's3IA',
            label: constants.AWS_S3_IA,
            content: generateCashFlowTable(
                generateS3IACashFlowItem(storageAnnualCost.s3IA),
                storageAnnualCost.s3IA.length,
                currency,
                constants.AWS_S3_IA + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'network',
            label: constants.ASSETS_NETWORK,
            content: generateCashFlowTable(
                generateNetworkCashFlowItems(network),
                network.cashFlow.yearly.length,
                currency,
                constants.ASSETS_NETWORK + ' ' + constants.DETAILS,
            ),
        },
        {
            id: 'ebsVolumePerServer',
            label: constants.AWS_EBS_VOLUME,
            content: buildEbsVolumePerServerTable(serverStorage, currency),
        },
        {
            id: 'storageRecommendation',
            label: constants.AWS_DB_RECOMMENDATION,
            content: buildStorageRecommendationTable(storageRecommendations, currency),
        }
    ];

    if (storageAnnualCost.rdsGeneralPurposeAurora && storageAnnualCost.rdsGeneralPurpose && storageAnnualCost.rdsProvisionedIops) {
        const rdsTabs: TabsProps.Tab[] = [
            {
                id: 'rdsGeneralPurpose',
                label: constants.AWS_RDS_GP,
                content: generateCashFlowTable(
                    generaterRdsCashFlowItem(storageAnnualCost.rdsGeneralPurpose),
                    storageAnnualCost.rdsGeneralPurpose.length,
                    currency,
                    constants.AWS_RDS_GP + ' ' + constants.DETAILS,
                ),
            },
            {
                id: 'rdsGeneralPurposeAurora',
                label: constants.AWS_RDS_GP_AURORA,
                content: generateCashFlowTable(
                    generaterRdsCashFlowItem(storageAnnualCost.rdsGeneralPurposeAurora),
                    storageAnnualCost.rdsGeneralPurposeAurora.length,
                    currency,
                    constants.AWS_RDS_GP_AURORA + ' ' + constants.DETAILS,
                ),
            },
            {
                id: 'rdsProvisionedIops',
                label: constants.AWS_RDS_PROVISIONED,
                content: generateCashFlowTable(
                    generaterRdsCashFlowItem(storageAnnualCost.rdsProvisionedIops, true),
                    storageAnnualCost.rdsProvisionedIops.length,
                    currency,
                    constants.AWS_RDS_PROVISIONED + ' ' + constants.DETAILS,
                ),
            },
        ];
        storageAndNetworkTabs.splice(5, 0, ...rdsTabs);
    }
    
    return storageAndNetworkTabs;
};
